import Cookies from 'js-cookie';

export type ApiResponse<T = any> = {
  success: true;
  data: T;
} | {
  success: false;
  error: string;
  code?: number;
};

export interface ApiConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;

  constructor(config: ApiConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.timeout = config.timeout || 30000;
    this.defaultHeaders = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...config.headers,
    };
  }

  private async request<T>(
    method: string,
    url: string,
    data?: any,
    headers?: Record<string, string>,
    withToken: boolean = false
  ): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const fullUrl = this.baseURL + url;
      
      const requestHeaders: Record<string, string> = {
        ...this.defaultHeaders,
        ...headers,
      };

      const config: RequestInit = {
        method,
        headers: requestHeaders,
        signal: controller.signal,
      };

      if (data && method !== 'GET' && method !== 'HEAD') {
        config.body = JSON.stringify(data);
      }

      const response = await fetch(fullUrl, config);
      clearTimeout(timeoutId);

      // Handle unauthorized access ONLY for requests that should have tokens
      if (response.status === 401 && withToken) {
        // Clear token from cookies
        Cookies.remove('token');
        
        // Clear user data from localStorage/sessionStorage if exists
        localStorage.removeItem('user_data');
        sessionStorage.removeItem('user_data');
        
        // Redirect to login page
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          // Store the current path to redirect back after login
          sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
          window.location.href = '/login';
        }
        
        return {
          success: false,
          error: 'Session expired. Please log in again.',
          code: 401,
        };
      }

      const responseData = await response.json().catch(() => null);

      if (response.ok) {
        return {
          success: true,
          data: responseData,
        };
      } else {
        const errorMessage = this.parseError(responseData, response.status);
        return {
          success: false,
          error: errorMessage,
          code: response.status,
        };
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: 'Request timeout',
            code: -1,
          };
        }
        
        return {
          success: false,
          error: error.message || 'Network error occurred',
          code: -1,
        };
      }
      
      return {
        success: false,
        error: 'Unknown error occurred',
        code: -1,
      };
    }
  }

  private parseError(errorBody: any, errorCode: number): string {
    if (!errorBody) {
      return `An unknown error occurred (Code: ${errorCode})`;
    }

    // Handle Laravel-style errors
    if (typeof errorBody === 'object') {
      if (errorBody.error) {
        return errorBody.error;
      }
      if (errorBody.message) {
        return errorBody.message;
      }
      if (errorBody.errors) {
        const errors = errorBody.errors;
        const messages: string[] = [];
        
        if (typeof errors === 'object') {
          Object.keys(errors).forEach(key => {
            if (Array.isArray(errors[key])) {
              messages.push(...errors[key]);
            } else {
              messages.push(errors[key]);
            }
          });
        }
        
        return messages.join(', ') || `Validation errors occurred (Code: ${errorCode})`;
      }
    }

    return `An error occurred (Code: ${errorCode})`;
  }

  // GET request (no automatic token)
  async get<T = any>(url: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, undefined, headers, false);
  }

  // POST request (no automatic token)
  async post<T = any>(url: string, data?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, headers, false);
  }

  // PUT request (no automatic token)
  async put<T = any>(url: string, data?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, headers, false);
  }

  // DELETE request (no automatic token)
  async delete<T = any>(url: string, data?: any, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, data, headers, false);
  }

  // GET request with token (explicit token passing)
  async getWithToken<T = any>(
    url: string, 
    token: string, 
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, undefined, {
      ...headers,
      'Authorization': `Bearer ${token}`,
    }, true);
  }

  // POST request with token (explicit token passing)
  async postWithToken<T = any>(
    url: string, 
    token: string, 
    data?: any, 
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, {
      ...headers,
      'Authorization': `Bearer ${token}`,
    }, true);
  }

  // PUT request with token (explicit token passing)
  async putWithToken<T = any>(
    url: string, 
    token: string, 
    data?: any, 
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, {
      ...headers,
      'Authorization': `Bearer ${token}`,
    }, true);
  }

  // DELETE request with token (explicit token passing)
  async deleteWithToken<T = any>(
    url: string, 
    token: string, 
    data?: any, 
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, data, {
      ...headers,
      'Authorization': `Bearer ${token}`,
    }, true);
  }

  // File upload helper (no automatic token)
  async uploadFile<T = any>(
    url: string,
    file: File,
    formDataName: string = 'file',
    additionalData?: Record<string, any>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append(formDataName, file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const uploadHeaders: Record<string, string> = {
      ...headers,
    };

    // Remove JSON headers for FormData
    delete uploadHeaders['Content-Type'];

    return this.request<T>('POST', url, formData, uploadHeaders, false);
  }

  // File upload with token (explicit token passing)
  async uploadFileWithToken<T = any>(
    url: string,
    file: File,
    token: string,
    formDataName: string = 'file',
    additionalData?: Record<string, any>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append(formDataName, file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const uploadHeaders: Record<string, string> = {
      ...headers,
      'Authorization': `Bearer ${token}`,
    };

    // Remove JSON headers for FormData
    delete uploadHeaders['Content-Type'];

    return this.request<T>('POST', url, formData, uploadHeaders, true);
  }
}

// Create default instance
export const apiClient = new ApiClient({
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 30000,
});

export default apiClient;